import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Tabs,
  Tooltip,
  notification,
  message,
  Tag,
  Alert,
  Drawer,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  HourglassOutlined,
  MoreOutlined,
  MessageOutlined,
  ReloadOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { formatAppNumber, getIntlLocale } from "util/locale";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiRequest } from "util/services";
import { readWorkspaceDeepLinkFromSearch, buildAiReportApiRequestPath } from "util/workspaceDeepLink";
import { trackAiReportOpenedOnce } from "util/productAnalytics";
import {
  formatAiReportCoverageLabel,
  formatWidgetBreakdownLabel,
  reportDisplayStatus,
  reportHasActiveWidgetJobs,
  reportMetricChipTone,
  reportMetricStatus,
} from "util/aiReportWidgetBreakdown";
import { AiReportWidgetBreakdownChips } from "components/AiServices/AiReportWidgetBreakdownChips";
import { welcomeRegenerateCanProceedWithoutQuotaModal } from "util/quotaCheckDisplay";
import { formatInUserTimezone } from "util/formatInUserTimezone";
import dayjs from "dayjs";
import styles from "./style.module.scss";
import welcomeStyles from "pages/Welcome/style.module.scss";
import { WelcomeOrgPromptEditorModal } from "components/WelcomePage/WelcomeOrgPromptEditorModal";
import { useEnterprisePowerUser } from "hooks/useEnterprisePowerUser";
import { WelcomeWidgetInfoModal } from "components/WelcomePage/WelcomeWidgetInfoModal";
import { mergeWidgetInfoPayload } from "pages/Welcome/welcomeWidgetInfoLabels";
import { WelcomeWidgetRegenerateQuotaModal } from "components/WelcomePage/WelcomeWidgetRegenerateQuotaModal";
import {
  WelcomeGenaiFeedback,
  welcomeWidgetSupportsGenaiFeedback,
} from "components/WelcomePage/WelcomeGenaiFeedback";

// ── Welcome rendering components (same look as My Workspace) ─────────────────
import {
  WelcomeGeneralSummaryNarrative,
  WelcomeHierarchicalSummaryReadOnly,
  WelcomeRootCauseCardStack,
  WelcomeGenaiRootCauseTopicTabs,
  WelcomeHotTermsWidget,
  WelcomeDecisionDistribution,
  WELCOME_HIERARCHICAL_SUMMARY_METRIC_IDS,
  WELCOME_GENERAL_SUMMARY_METRIC_ID,
  WELCOME_GENAI_TABLE_METRIC_IDS,
  WELCOME_GENAI_NARRATIVE_METRIC_IDS,
  getWidgetTableSchema,
  normalizeRowsBySchema,
  decodeBasicHtmlEntities,
  buildWelcomeWidgetTitleLine,
  DECISION_DISTRIBUTION_CONFIG,
  collectGenaiItemReviewIds,
  parseTopicLibIdFromWidgetTab,
} from "pages/Welcome";
import HighlightMetric25TabEditorPane from "pages/Welcome/HighlightMetric25TabEditorPane";
import KeyDriverMetric23TabEditorPane from "pages/Welcome/KeyDriverMetric23TabEditorPane";
import KPIDoughnutChart from "../../../components/WelcomePage/KPIDoughnutChart";
import KPIStackedBarChart from "../../../components/WelcomePage/KPIStackedBarChart";
import WelcomeTopicSentimentBarChart from "../../../components/WelcomePage/WelcomeTopicSentimentBarChart";
import WelcomeTopicParticipationBarChart from "../../../components/WelcomePage/WelcomeTopicParticipationBarChart";
import WelcomeTopicIntentBarChart from "../../../components/WelcomePage/WelcomeTopicIntentBarChart";
import KPIBarChart from "../../../components/WelcomePage/KPIBarChart";
import WelcomeBrandKpiHeroCard from "../../../components/WelcomePage/WelcomeBrandKpiHeroCard";
import {
  resolveBrandKpiHeroHeader,
  welcomeWidgetUsesBrandKpiHero,
  welcomeWidgetUsesDoubleGridColumn,
} from "../../../components/WelcomePage/welcomeBrandKpiHero";
import WelcomeDualSentimentDoughnutCharts from "../../../components/WelcomePage/WelcomeDualSentimentDoughnutCharts";
import WelcomeDualIntentDoughnutCharts from "../../../components/WelcomePage/WelcomeDualIntentDoughnutCharts";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import {
  WelcomeWidgetDataStateMessage,
  WelcomeWidgetGenaiPendingState,
  WelcomeWidgetInsufficientOrEmpty,
  WELCOME_WIDGET_DATA_STATUS_EMPTY,
  WELCOME_WIDGET_DATA_STATUS_FAILED,
  WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT,
  isWelcomeGenAiContentMetricWidget,
  resolveWelcomeWidgetDataState,
  welcomeKpiErrorMeansNoData,
  welcomeWidgetJobIsProcessing,
  welcomeGenaiRegenerateSkipsCreditCharge,
  resolveWelcomeJobStatusDescription,
  resolveWelcomeInsufficientReviewCounts,
  welcomeRegenerateSkipsQuotaCheck,
  welcomeJobStatusDescriptionMeansEmptyGenaiOutput,
  welcomeWidgetFailedJobShowsContentState,
  welcomeWidgetShowsEmptyGenaiSignal,
  welcomeWidgetShowsInsufficientState,
} from "pages/Welcome/welcomeWidgetDataState";
import { resolveWelcomeWidgetMinReviewCount } from "constants/welcomeWidgetAnalysis";
import { getWelcomeWidgetJobProgressClassNames, isWelcomeWidgetJobStuckInProgress } from "pages/Welcome/welcomeWidgetJobTiming";

const welcomeWidgetJobProgressClassNames =
  getWelcomeWidgetJobProgressClassNames(welcomeStyles);

const { TextArea } = Input;

/** Limit parallel widget-kpi-data POSTs so many report widgets do not stampede the API (504 from nginx). */
const AI_REPORT_WIDGET_KPI_FETCH_CONCURRENCY = 3;
let _aiReportKpiFetchActive = 0;
const _aiReportKpiFetchWaitQueue = [];

function _releaseAiReportWidgetKpiFetchSlot() {
  _aiReportKpiFetchActive -= 1;
  const next = _aiReportKpiFetchWaitQueue.shift();
  if (next) next();
}

/** Global slot pool shared by all widgets on the AI report detail page. */
function withAiReportWidgetKpiFetchSlot(fn) {
  return new Promise((resolve, reject) => {
    const run = async () => {
      _aiReportKpiFetchActive += 1;
      try {
        resolve(await fn());
      } catch (e) {
        reject(e);
      } finally {
        _releaseAiReportWidgetKpiFetchSlot();
      }
    };
    if (_aiReportKpiFetchActive < AI_REPORT_WIDGET_KPI_FETCH_CONCURRENCY) {
      void run();
    } else {
      _aiReportKpiFetchWaitQueue.push(() => {
        void run();
      });
    }
  });
}

/** Read workspace_user_id from the current AI report deep-link URL. */
function aiReportWorkspaceUserIdFromLocation() {
  if (typeof window === "undefined") return null;
  return readWorkspaceDeepLinkFromSearch(window.location.search).workspaceUserId;
}

function aiReportApiUrl(reportId, suffix = "") {
  return buildAiReportApiRequestPath(
    reportId,
    suffix,
    aiReportWorkspaceUserIdFromLocation(),
  );
}

function fetchAiReportWidgetKpiData(reportId, widgetId) {
  return apiRequest(
    aiReportApiUrl(reportId, "/widget-kpi-data"),
    "POST",
    { widget_id: widgetId },
    true,
  );
}

function fetchAiReportWidgetInsight(reportId, widgetId) {
  return apiRequest(
    aiReportApiUrl(reportId, "/widget-insight"),
    "POST",
    { widget_id: widgetId },
    true,
  );
}

/** Same display names as My Workspace (`welcome.metricName_*`). Metric 15 = topic participation, 28 = hot terms. */
function welcomeMetricTitle(metricId, t) {
  const id = Number(metricId);
  if (!Number.isFinite(id) || id < 1) return null;
  const key = `welcome.metricName_${id}`;
  const label = t(key);
  return label !== key ? label : null;
}

function reportMetricChipLabel(metricId, t) {
  const id = Number(metricId);
  if (!Number.isFinite(id) || id < 1) {
    return metricId != null && metricId !== "" ? String(metricId) : "";
  }
  const key = `welcome.metricName_${id}`;
  const label = t(key);
  return label !== key ? label : `Metric ${id}`;
}

function renderReportInfoMetricChip(report, mid, t) {
  const tone = reportMetricChipTone(reportMetricStatus(report, mid));
  const chipClass = [
    styles.reportInfoMetricChip,
    tone === "ready" ? styles.reportInfoMetricChipReady : null,
    tone === "problem" ? styles.reportInfoMetricChipProblem : null,
    tone === "inProgress" ? styles.reportInfoMetricChipInProgress : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <span key={mid} className={chipClass}>
      <span>{reportMetricChipLabel(mid, t)}</span>
      {tone === "ready" ? (
        <CheckOutlined className={styles.reportInfoMetricIconReady} aria-hidden />
      ) : null}
      {tone === "problem" ? (
        <CloseOutlined className={styles.reportInfoMetricIconProblem} aria-hidden />
      ) : null}
      {tone === "inProgress" ? (
        <HourglassOutlined className={styles.reportInfoMetricIconProgress} aria-hidden />
      ) : null}
    </span>
  );
}

/** Console session dashboard name + optional API `welcome_group_name` (My Workspace group). */
function reportDashboardContextPrimary(report, sessionDashboardName) {
  const session = String(sessionDashboardName ?? "").trim();
  const ws = String(report?.welcome_group_name ?? "").trim();
  if (session && ws) return `${session} · ${ws}`;
  if (session) return session;
  if (ws) return ws;
  return "";
}

function reportDashboardContextTitle(report, sessionDashboardName, t) {
  const session = String(sessionDashboardName ?? "").trim();
  const ws = String(report?.welcome_group_name ?? "").trim();
  if (!session && !ws) return t("ai_services.reports_session_dashboard_none");
  if (!session && ws) return t("ai_services.reports_workspace_group_hint");
  return undefined;
}

/** Read-only topic tabs for brand chart KPIs (intent, sentiment, participation) — mirrors My Workspace payloads. */
function BrandKpiTopicTabsReadOnly({ data, theme, metricId, widget, reportId }) {
  const { t, i18n } = useTranslation();
  const tabs = data.topic_tabs || [];
  if (!tabs.length) return null;

  return (
    <Tabs
      className={styles.aiReportTopicTabs}
      items={tabs.map((tab) => {
        const tp = tab.payload || {};
        const tabPending = Boolean(tab.payload_pending && tab.payload == null);
        const stackedBarData =
          (Number(metricId) === 14 || Number(metricId) === 12) &&
          tab.id === "general" &&
          tp.chart_type === "stacked_bar" &&
          Array.isArray(tp.labels)
            ? {
                ...tp,
                labels: tp.labels.map((lb) =>
                  lb === "Average" ? t("welcome.topicSentimentGeneralBarLabel") : lb,
                ),
              }
            : tp;
        const customLabels =
          tp.legend && Array.isArray(tp.legend)
            ? tp.legend.map((item) => item.count)
            : null;
        const tabRcRaw = tab?.review_count;
        const hasNumericTopicReviewCount =
          tabRcRaw != null && tabRcRaw !== "" && Number.isFinite(Number(tabRcRaw));
        const topicReviewCountLabel = hasNumericTopicReviewCount
          ? new Intl.NumberFormat(
              getIntlLocale(i18n.language),
            ).format(Number(tabRcRaw))
          : null;
        const brandKpiHeroHeader = resolveBrandKpiHeroHeader(tp, metricId);
        const isBrandKpiHero =
          welcomeWidgetUsesBrandKpiHero({ metric_id: metricId }) &&
          brandKpiHeroHeader != null;
        const tabHeader = isBrandKpiHero
          ? {
              ...brandKpiHeroHeader,
              review_count:
                tabRcRaw != null &&
                tabRcRaw !== "" &&
                Number.isFinite(Number(tabRcRaw))
                  ? Number(tabRcRaw)
                  : brandKpiHeroHeader?.review_count,
            }
          : tp.header;

        return {
          key: String(tab.id),
          label:
            tab.id === "general"
              ? t("welcome.genaiMultiTopicGeneralTab")
              : tab.label || String(tab.id),
          children: tabPending ? (
            <div className={styles.contentPreviewLoading} style={{ padding: 24 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {typeof tp.error === "string" && tp.error.trim() ? (
                <div style={{ color: "#888", fontSize: 13, padding: "8px 0" }}>{tp.error.trim()}</div>
              ) : (
              <>
              {topicReviewCountLabel != null && (
                <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                  {t("welcome.hierarchicalTabReviewCount", {
                    count: topicReviewCountLabel,
                  })}
                </div>
              )}
              {isBrandKpiHero ? (
                <div
                  className={`${welcomeStyles.chartContainer} ${welcomeStyles.brandKpiHeroContainer}`}
                >
                  <WelcomeBrandKpiHeroCard
                    metricId={metricId}
                    header={tabHeader}
                    widget={widget}
                    since={widget.since}
                    until={widget.until}
                    t={t}
                    i18n={i18n}
                    isPageDataLoading={false}
                  />
                </div>
              ) : (
              <div>
                {tp.chart_type === "topic_doughnut_grid" &&
                  Array.isArray(tp.topics) &&
                  tp.topics.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 24,
                        justifyContent: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      {tp.topics.map((topic, ti) => {
                        const td = {
                          labels: topic.labels,
                          legend: topic.legend,
                          datasets: topic.datasets,
                        };
                        const cl =
                          topic.legend && Array.isArray(topic.legend)
                            ? topic.legend.map((item) => item.count)
                            : null;
                        if (!td.datasets?.[0]?.data?.length) return null;
                        return (
                          <div
                            key={`${topic.name || "t"}-${ti}`}
                            style={{ textAlign: "center", maxWidth: 280 }}
                          >
                            <KPIDoughnutChart
                              data={td}
                              theme={theme}
                              customLabels={cl}
                              showLegend={Number(metricId) === 12}
                            />
                            <div style={{ marginTop: 8, fontWeight: 600 }}>
                              {topic.name}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {tp.chart_type === "doughnut" && (
                  <KPIDoughnutChart
                    data={tp}
                    theme={theme}
                    customLabels={customLabels}
                    showLegend={Number(metricId) === 12}
                  />
                )}
                {tp.chart_type === "bar" &&
                  (Number(metricId) === 15 ? (
                    <WelcomeTopicParticipationBarChart payload={tp} />
                  ) : (
                    <KPIBarChart data={tp} theme={theme} />
                  ))}
                {tp.chart_type === "dual_doughnut_sentiment" && (
                  <WelcomeDualSentimentDoughnutCharts data={tp} theme={theme} />
                )}
                {tp.chart_type === "dual_doughnut_intent" && (
                  <WelcomeDualIntentDoughnutCharts data={tp} theme={theme} />
                )}
                {tp.chart_type === "stacked_bar" &&
                  (Number(metricId) === 14 ? (
                    <WelcomeTopicSentimentBarChart
                      payload={stackedBarData}
                      widgetId={widget?.id}
                      reportId={reportId}
                    />
                  ) : Number(metricId) === 12 ? (
                    <WelcomeTopicIntentBarChart payload={stackedBarData} />
                  ) : (
                    <KPIStackedBarChart data={stackedBarData} theme={theme} />
                  ))}
              </div>
              )}
              </>
              )}
            </>
          ),
        };
      })}
    />
  );
}

/** Stable poll dependency: only changes when processing widget count or report phase changes. */
function reportPollActivityKey(report) {
  if (!report) return null;
  const widgets = Array.isArray(report.widgets) ? report.widgets : [];
  let processingCount = 0;
  for (const w of widgets) {
    if (welcomeWidgetJobIsProcessing(w?.status)) processingCount += 1;
  }
  const pdfActive =
    report.pdf_status === "Pending" || report.pdf_status === "Processing";
  return `${report.status}|${report.pdf_status}|${processingCount}|${pdfActive ? 1 : 0}`;
}

function reportPollNeedsPolling(reportPollKey) {
  if (!reportPollKey) return false;
  const parts = reportPollKey.split("|");
  const status = parts[0];
  const pdfStatus = parts[1];
  const processingCount = Number(parts[2]) || 0;
  const pdfActive = parts[3] === "1";
  return (
    status === "finalizing" ||
    pdfStatus === "Pending" ||
    pdfStatus === "Processing" ||
    pdfActive ||
    processingCount > 0
  );
}

/** Prefer full API header (breakdown) over legacy repr-3 list normalization. */
function resolveAiReportBrandKpiHeader(data, metricId) {
  const mid = Number(metricId);
  const normalized = normalizeAiReportBrandKpiData(data, metricId);
  const heroHeader = resolveBrandKpiHeroHeader(normalized, mid);
  if (!heroHeader) return null;
  const rawHeader = data?.header;
  if (rawHeader && typeof rawHeader === "object") {
    if (mid === 10 && rawHeader.sentiment_breakdown) return rawHeader;
    if (mid === 29 && rawHeader.intent_breakdown) return rawHeader;
    return { ...heroHeader, ...rawHeader };
  }
  return heroHeader;
}

/** Repr=3 legacy list payloads for metrics 10/29 → hero header shape. */
function normalizeAiReportBrandKpiData(data, metricId) {
  const mid = Number(metricId);
  if (!data || typeof data !== "object") return data;
  if (resolveBrandKpiHeroHeader(data, mid)) return data;

  if (mid !== 10 && mid !== 29) return data;

  const overallRaw = data.overall_score;
  if (overallRaw != null && overallRaw !== "") {
    const pct = Number(String(overallRaw).replace(/%/g, "").trim());
    if (Number.isFinite(pct)) {
      return {
        ...data,
        header: {
          value: pct,
          title:
            mid === 10 ? "Positive Sentiment Score" : "Praise Intent Score",
          unit: "%",
        },
      };
    }
  }

  const items = Array.isArray(data.items) ? data.items : [];
  if (items.length > 0 && items[0]?.dashboard && items[0]?.score) {
    const synthetic = {
      ...data,
      items: [
        {
          title:
            mid === 10
              ? "Overall Sentiment Score"
              : "Overall Praise Intent Score",
          description: data.overall_score ?? items[0].score,
        },
      ],
    };
    if (resolveBrandKpiHeroHeader(synthetic, mid)) return synthetic;
  }

  return data;
}

const POLL_MS = 8000;

const JOB_STATUS = { 1: "pending", 2: "in_progress", 3: "completed", 4: "failed", 5: "skipped" };

function statusLabel(s) {
  if (!s && s !== 0) return null;
  return JOB_STATUS[s] || String(s);
}

function canRegenerateStuckWidget(widget) {
  if (!isAiReportRegenerableWidget(widget)) return false;
  const s = statusLabel(widget?.status);
  return s === "failed" || isWelcomeWidgetJobStuckInProgress(widget);
}

// ─── Insight Item Card (edit modal sub-component) ────────────────────────────
function InsightCard({ item, idx, onChange, showDelete, onDeleteClick, readOnly }) {
  const { t } = useTranslation();
  const fields = Object.entries(item).filter(([k]) => k !== "id" && k !== "order");

  return (
    <div className={styles.insightCard}>
      <div className={styles.insightCardHeader}>
        <div className={styles.insightCardHeaderMain}>
          <span className={styles.insightCardNum}>#{idx + 1}</span>
          {(item.driver_label || item.highlight_title || item.label || item.topic) && (
            <span className={styles.insightCardTitle}>
              {item.driver_label || item.highlight_title || item.label || item.topic}
            </span>
          )}
        </div>
        {showDelete && !readOnly && (
          <Tooltip title={t("common.delete")}>
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              className={styles.insightCardDelete}
              aria-label={t("common.delete")}
              onClick={() => onDeleteClick(idx, item)}
            />
          </Tooltip>
        )}
      </div>
      <div className={styles.insightCardFields}>
        {fields.map(([key, val]) => (
          <div key={key} className={styles.insightCardField}>
            <label className={styles.insightCardLabel}>{key.replace(/_/g, " ")}</label>
            <TextArea
              value={val ?? ""}
              readOnly={readOnly}
              autoSize={{ minRows: 2, maxRows: 6 }}
              onChange={(e) => onChange(idx, key, e.target.value)}
              className={styles.insightCardInput}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Edit Content Modal ──────────────────────────────────────────────────────
function EditInsightModal({
  reportId,
  widget,
  onClose,
  onSaved,
  contentEditable = true,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [kind, setKind] = useState(null);
  const [items, setItems] = useState([]);
  const [narrativeText, setNarrativeText] = useState("");

  useEffect(() => {
    if (!widget) return;
    fetchInsight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget]);

  const fetchInsight = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchAiReportWidgetInsight(reportId, widget.id);
      if (res?.success) {
        const { insight_kind, items: rawItems } = res.success;
        setKind(insight_kind);
        if (insight_kind === "brand_genai_narrative") {
          const narrative =
            Array.isArray(rawItems) && rawItems.length > 0
              ? rawItems[0]?.description ?? rawItems[0]?.text ?? rawItems[0]?.content ?? ""
              : "";
          setNarrativeText(narrative);
        } else {
          setItems(rawItems || []);
        }
      } else {
        setError(res?.error || t("ai_services.load_error"));
      }
    } catch {
      setError(t("ai_services.load_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleCardChange = (idx, key, val) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [key]: val } : item)));
  };

  const confirmDeleteCard = (idx, item) => {
    if (!contentEditable) return;
    Modal.confirm({
      title: t("ai_services.root_cause_delete_confirm_title"),
      content: t("ai_services.root_cause_delete_confirm_desc"),
      okText: t("common.delete"),
      okType: "danger",
      cancelText: t("common.cancel"),
      centered: true,
      onOk: async () => {
        const rcRaw = item.rc_id ?? item.ID;
        const rcId = rcRaw != null && String(rcRaw).trim() !== "" ? String(rcRaw).trim() : "";
        const deleteBody = { widget_id: widget.id };
        if (rcId) deleteBody.rc_id = rcId;
        else deleteBody.item_index = idx;
        const res = await apiRequest(
          aiReportApiUrl(reportId, "/widget-insight-items/delete"),
          "POST",
          deleteBody,
          true,
        );
        if (res && res.success) {
          message.success(t("ai_services.root_cause_deleted"));
          setItems((prev) => prev.filter((_, i) => i !== idx));
          onSaved();
          return;
        }
        message.error(t("ai_services.root_cause_delete_error"));
        throw new Error("delete failed");
      },
    });
  };

  const handleSave = async () => {
    if (!contentEditable) return;
    setSaving(true);
    try {
      let payload;
      if (kind === "brand_genai_narrative") {
        payload = [{ description: narrativeText }];
      } else {
        payload = items;
      }
      const res = await apiRequest(
        aiReportApiUrl(reportId, "/widget-insight-items"),
        "PATCH",
        { widget_id: widget.id, items: payload }
      );
      if (res?.success) {
        message.success(t("ai_services.widget_saved"));
        onSaved();
        onClose();
      } else {
        message.error(res?.error || t("ai_services.save_error"));
      }
    } catch {
      message.error(t("ai_services.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!widget}
      onCancel={onClose}
      title={
        <span>
          <EditOutlined style={{ marginRight: 8 }} />
          {welcomeMetricTitle(widget?.metric_id, t) || widget?.name || t("ai_services.edit_content")}
        </span>
      }
      width={720}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {!contentEditable ? (
            <Alert type="info" showIcon message={t("welcome.editContentReadOnlyUser")} />
          ) : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button key="cancel" onClick={onClose}>{t("cancel")}</Button>
            {contentEditable ? (
              <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                {t("save")}
              </Button>
            ) : null}
          </div>
        </div>
      }
      className={styles.editModal}
    >
      {loading ? (
        <div className={styles.editModalLoading}><Spin /></div>
      ) : error ? (
        <div className={styles.editModalError}>{error}</div>
      ) : kind === "brand_genai_narrative" ? (
        <div className={styles.narrativeEditor}>
          <label className={styles.narrativeLabel}>{t("ai_services.narrative_content")}</label>
          <TextArea
            value={narrativeText}
            readOnly={!contentEditable}
            onChange={(e) => setNarrativeText(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 20 }}
            className={styles.narrativeTextarea}
          />
        </div>
      ) : (
        <div className={styles.cardsEditor}>
          <p className={styles.cardsEditorNote}>
            {t("ai_services.cards_editor_note", { count: items.length })}
          </p>
          <div className={styles.cardsList}>
            {items.map((item, idx) => (
              <InsightCard
                key={`${item.rc_id ?? item.ID ?? "row"}-${idx}`}
                item={item}
                idx={idx}
                onChange={handleCardChange}
                showDelete={kind === "brand_genai_table"}
                readOnly={!contentEditable}
                onDeleteClick={confirmDeleteCard}
              />
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Hierarchical summary tabs helper ────────────────────────────────────────
function hasHierarchicalSummaryTabs(kpiData, metricId) {
  if (!kpiData) return false;
  const data = kpiData.data || {};
  let sumTabs = data.summary_tabs;
  if (!Array.isArray(sumTabs)) return false;
  if (Number(metricId) === 25) {
    sumTabs = sumTabs.filter(
      (tab) => tab && !["general", "global"].includes(String(tab.id || "").toLowerCase())
    );
  }
  const reprType = Number(kpiData.representation_type || 0);
  return sumTabs.length > 1 && [21, 23, 25].includes(Number(metricId)) && reprType === 4;
}

/** Prefer Mongo-persisted tabs for edit/save (skip KPI-synthesized topic-N). */
function resolveEditableNarrativeSummaryTabs(data, metricId) {
  const payload = data && typeof data === "object" ? data : {};
  const editable = Array.isArray(payload.summary_tabs_editable)
    ? payload.summary_tabs_editable
    : null;
  let tabs = editable && editable.length > 0
    ? editable
    : Array.isArray(payload.summary_tabs)
      ? payload.summary_tabs
      : [];
  if (Number(metricId) === 25) {
    tabs = tabs.filter(
      (tab) =>
        tab && !["general", "global"].includes(String(tab.id || "").toLowerCase()),
    );
  }
  // Drop synthetic topic-1..N when editable list was empty / missing.
  if (!editable || editable.length === 0) {
    tabs = tabs.filter((tab) => {
      const id = String(tab?.id || "");
      const lower = id.toLowerCase();
      if (lower === "general" || lower === "global") return true;
      if (/^topic_\d+$/i.test(id)) return true;
      if (/^topic-\d+$/i.test(id)) return false;
      return Boolean(id);
    });
  }
  return tabs;
}

function normalizeWidgetIdList(value) {
  const raw = Array.isArray(value) ? value : value != null ? [value] : [];
  return raw.map((item) => String(item).trim()).filter((item) => item !== "");
}

function buildAiReportRegenerateQuotaPayload(widget, report, widgetKpiData) {
  const data = widgetKpiData?.data || {};
  const dashboardIds = normalizeWidgetIdList(
    widget?.dashboard_ids || data?.source_dashboard_ids,
  );
  let since = widget?.since || data?.source_since || null;
  let until = widget?.until || data?.source_until || null;
  if ((!since || !until) && report?.report_month) {
    const m = dayjs(report.report_month);
    if (!since) since = m.startOf("month").format("YYYY-MM-DD");
    if (!until) until = m.endOf("month").format("YYYY-MM-DD");
  }
  const payload = {
    quota_type: "advanced_ai",
    dashboard_ids: dashboardIds,
    start_date: since,
    end_date: until,
    metric_type: 2,
  };
  if (widget?.pivot_key && widget?.pivot_values != null) {
    payload.pivot_key = widget.pivot_key;
    payload.pivot_values = normalizeWidgetIdList(widget.pivot_values);
  } else if (data?.source_pivot_key && data?.source_pivot_values != null) {
    payload.pivot_key = data.source_pivot_key;
    payload.pivot_values = normalizeWidgetIdList(data.source_pivot_values);
  }
  const topicIds = normalizeWidgetIdList(widget?.topic_ids || data?.topic_ids)
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (topicIds.length > 0) {
    payload.topic_ids = topicIds;
  }
  const mid = Number(widget?.metric_id);
  if (Number.isFinite(mid) && mid > 0) {
    payload.welcome_metric_ids = [mid];
  }
  return payload;
}

function widgetSupportsOrgLlmPromptAi(w) {
  const mid = w?.metric_id;
  const rep = w?.representation_type;
  if (mid == null || rep == null) return false;
  if (WELCOME_GENAI_NARRATIVE_METRIC_IDS.has(Number(mid)) && Number(rep) === 4) {
    return true;
  }
  if (WELCOME_GENAI_TABLE_METRIC_IDS.has(Number(mid)) && Number(rep) === 3) {
    return true;
  }
  return false;
}

function canEditInsightAiReport(merged, hasHierTabs) {
  if (hasHierTabs) return false;
  const metricId = Number(merged?.metric_id);
  const repr = Number(merged?.representation_type);
  const isNarrative =
    WELCOME_GENAI_NARRATIVE_METRIC_IDS.has(metricId) && (repr === 4 || !repr);
  const isTable =
    WELCOME_GENAI_TABLE_METRIC_IDS.has(metricId) && (repr === 3 || !repr);
  return isNarrative || isTable;
}

function isAiReportRegenerableWidget(w) {
  return Number.isFinite(Number(w?.metric_id)) && Number(w?.metric_id) > 0;
}

// ─── Edit Summary Tabs Modal ─────────────────────────────────────────────────
function EditSummaryTabsModal({ reportId, widget, onClose, onSaved }) {
  const { t } = useTranslation();
  const highlightsMetric25 = Number(widget?.metric_id) === 25;
  const keyDriversMetric23 = Number(widget?.metric_id) === 23;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    if (!widget) return;
    fetchTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget]);

  const fetchTabs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await withAiReportWidgetKpiFetchSlot(() =>
        fetchAiReportWidgetKpiData(reportId, widget.id),
      );
      if (res?.success) {
        const data = res.success.data || {};
        const tabs = resolveEditableNarrativeSummaryTabs(data, widget.metric_id);
        setDraft(
          tabs.map((tab) => ({
            id: String(tab.id),
            label: String(tab.label || tab.id),
            content: String(tab.content ?? ""),
          }))
        );
      } else {
        setError(res?.error || t("ai_services.load_error"));
      }
    } catch {
      setError(t("ai_services.load_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiRequest(
        "welcome/widget/narrative-tabs",
        "PUT",
        {
          widget_id: widget.id,
          updates: draft.map((row) => ({ tab_id: row.id, content: row.content })),
        },
        true
      );
      if (res?.success) {
        message.success(t("welcome.summaryTabsSaveSuccess"));
        onSaved();
        onClose();
      } else {
        message.error(res?.error || t("welcome.summaryTabsSaveError"));
      }
    } catch {
      message.error(t("welcome.summaryTabsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!widget}
      onCancel={onClose}
      title={t("welcome.editSummaryTabs")}
      width={920}
      centered
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>{t("cancel")}</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {t("save")}
        </Button>,
      ]}
      className={styles.editModal}
    >
      {loading ? (
        <div className={styles.editModalLoading}><Spin /></div>
      ) : error ? (
        <div className={styles.editModalError}>{error}</div>
      ) : draft.length === 0 ? (
        <div className={styles.editModalError}>{t("ai_services.load_error")}</div>
      ) : (
        <Tabs
          items={draft.map((row) => ({
            key: row.id,
            label: row.label || row.id,
            children: highlightsMetric25 ? (
              <HighlightMetric25TabEditorPane
                key={row.id}
                content={row.content}
                onContentChange={(next) => {
                  const rid = row.id;
                  setDraft((prev) =>
                    prev.map((p) => (p.id === rid ? { ...p, content: next } : p)),
                  );
                }}
              />
            ) : keyDriversMetric23 ? (
              <KeyDriverMetric23TabEditorPane
                key={row.id}
                tabLabel={row.label || row.id}
                content={row.content}
                onContentChange={(next) => {
                  const rid = row.id;
                  setDraft((prev) =>
                    prev.map((p) => (p.id === rid ? { ...p, content: next } : p)),
                  );
                }}
              />
            ) : (
              <Input.TextArea
                value={row.content}
                onChange={(e) => {
                  const v = e.target.value;
                  const rid = row.id;
                  setDraft((prev) =>
                    prev.map((p) => (p.id === rid ? { ...p, content: v } : p)),
                  );
                }}
                autoSize={{ minRows: 16, maxRows: 40 }}
              />
            ),
          }))}
        />
      )}
    </Modal>
  );
}

// ─── Welcome Widget Body — same rendering as My Workspace ────────────────────
function WelcomeWidgetBody({ reportId, widget, refreshKey, onDataLoaded, onRefresh }) {
  const { t, i18n } = useTranslation();
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const lazyRootRef = useRef(null);
  /** When false, KPI is not fetched — wait until card is near viewport (lazy). */
  const [viewportReached, setViewportReached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [widgetData, setWidgetData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [decisionReviewsDrawerOpen, setDecisionReviewsDrawerOpen] = useState(false);
  const [decisionReviewsLoading, setDecisionReviewsLoading] = useState(false);
  const [decisionReviewsSelected, setDecisionReviewsSelected] = useState(null);
  const [decisionReviewsList, setDecisionReviewsList] = useState([]);
  const [rootCauseReviewsDrawerOpen, setRootCauseReviewsDrawerOpen] = useState(false);
  const [rootCauseReviewsLoading, setRootCauseReviewsLoading] = useState(false);
  const [rootCauseReviewsSelected, setRootCauseReviewsSelected] = useState(null);
  const [rootCauseReviewsList, setRootCauseReviewsList] = useState([]);
  const [scopeReviewsDrawerOpen, setScopeReviewsDrawerOpen] = useState(false);
  const [scopeReviewsLoading, setScopeReviewsLoading] = useState(false);
  const [scopeReviewsSelected, setScopeReviewsSelected] = useState(null);
  const [scopeReviewsList, setScopeReviewsList] = useState([]);

  useEffect(() => {
    if (viewportReached) return;
    if (typeof IntersectionObserver === "undefined") {
      setViewportReached(true);
      return;
    }
    const node = lazyRootRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setViewportReached(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: "280px 0px", threshold: 0 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [viewportReached]);

  useEffect(() => {
    if (!viewportReached) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const res = await withAiReportWidgetKpiFetchSlot(() =>
          fetchAiReportWidgetKpiData(reportId, widget.id),
        );
        if (!cancelled && res?.success) {
          setWidgetData(res.success);
          if (onDataLoaded) onDataLoaded(res.success);
        } else if (!cancelled) {
          setLoadFailed(true);
        }
      } catch {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [viewportReached, reportId, widget.id, refreshKey, onDataLoaded]);

  const fetchMetric27ReportReviews = useCallback(
    async (decisionKey, decisionTrue, topicLibId) => {
      if (!widget?.id) return;
      setDecisionReviewsLoading(true);
      const sliceCfg = DECISION_DISTRIBUTION_CONFIG.find((c) => c.key === decisionKey);
      const labels = sliceCfg
        ? sliceCfg.labels.map((k) => t(k))
        : [String(decisionKey)];
      const sliceLabel = decisionTrue ? labels[1] : labels[0];
      setDecisionReviewsSelected({
        label: sliceCfg ? t(sliceCfg.titleKey) : String(decisionKey),
        root_cause: sliceLabel,
      });
      setDecisionReviewsDrawerOpen(true);
      try {
        const body = {
          widget_id: widget.id,
          decision: decisionKey,
          decision_true: Boolean(decisionTrue),
        };
        if (topicLibId != null && Number.isFinite(Number(topicLibId))) {
          body.topic_lib_id = Number(topicLibId);
        }
        const response = await apiRequest(
          aiReportApiUrl(reportId, "/widget-metric-27-reviews"),
          "POST",
          body,
          true,
        );
        if (Array.isArray(response?.success)) {
          setDecisionReviewsList(response.success);
        } else {
          setDecisionReviewsList([]);
        }
      } catch {
        setDecisionReviewsList([]);
      } finally {
        setDecisionReviewsLoading(false);
      }
    },
    [reportId, widget, t],
  );

  const fetchMetric20ReportReviews = useCallback(
    async (sourceItem, row) => {
      if (!widget?.id) return;
      const reviewIds = collectGenaiItemReviewIds(sourceItem);
      setRootCauseReviewsLoading(true);
      setRootCauseReviewsSelected({
        label: row?.label || sourceItem?.label || sourceItem?.topic || "",
        root_cause: row?.root_cause || sourceItem?.root_cause || "",
      });
      setRootCauseReviewsDrawerOpen(true);
      if (!reviewIds.length) {
        setRootCauseReviewsList([]);
        setRootCauseReviewsLoading(false);
        message.info(t("welcome.rootCauseNoAssociatedReviews"));
        return;
      }
      try {
        const response = await apiRequest(
          aiReportApiUrl(reportId, "/widget-metric-20-reviews"),
          "POST",
          { widget_id: widget.id, review_ids: reviewIds },
          true,
        );
        setRootCauseReviewsList(
          Array.isArray(response?.success) ? response.success : [],
        );
      } catch {
        setRootCauseReviewsList([]);
        message.error(t("ai_services.load_error"));
      } finally {
        setRootCauseReviewsLoading(false);
      }
    },
    [reportId, widget, t],
  );

  const fetchMetric21ScopeReviews = useCallback(
    async ({ topicLibId, topicLabel, reviewCount } = {}) => {
      if (!widget?.id) return;
      setScopeReviewsLoading(true);
      setScopeReviewsSelected({
        label: topicLabel || widget?.name || widget?.title || "",
        root_cause: topicLabel || "",
      });
      setScopeReviewsDrawerOpen(true);
      try {
        const body = { widget_id: widget.id };
        if (topicLibId != null && Number.isFinite(Number(topicLibId))) {
          body.topic_lib_id = Number(topicLibId);
        }
        const rc = Number(reviewCount);
        if (Number.isFinite(rc) && rc > 0) {
          body.max_reviews = Math.min(Math.max(Math.round(rc), 1), 200);
        }
        const response = await apiRequest(
          aiReportApiUrl(reportId, "/widget-metric-21-reviews"),
          "POST",
          body,
          true,
        );
        setScopeReviewsList(
          Array.isArray(response?.success) ? response.success : [],
        );
      } catch {
        setScopeReviewsList([]);
        message.error(t("ai_services.load_error"));
      } finally {
        setScopeReviewsLoading(false);
      }
    },
    [reportId, widget, t],
  );

  const rootCauseReviewsDrawer = (
    <Drawer
      {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
      title={
        rootCauseReviewsSelected ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "bold" }}>{rootCauseReviewsSelected.label}</span>
            <span
              style={{
                fontSize: "12px",
                color: theme === "dark" ? "#8c8c8c" : "#666",
              }}
            >
              {rootCauseReviewsSelected.root_cause}
            </span>
            </div>
        ) : (
          "Reviews"
        )
      }
      onClose={() => {
        setRootCauseReviewsDrawerOpen(false);
        setRootCauseReviewsLoading(false);
        setRootCauseReviewsSelected(null);
        setRootCauseReviewsList([]);
      }}
      open={rootCauseReviewsDrawerOpen}
      width={welcomeReviewsDrawerWidth()}
    >
      <Spin spinning={rootCauseReviewsLoading}>
        <WelcomeDrawerReviewsList
          allReviews={rootCauseReviewsList}
          reviewsLoading={rootCauseReviewsLoading}
        />
      </Spin>
    </Drawer>
  );

  const scopeReviewsDrawer = (
    <Drawer
      {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
      title={
        scopeReviewsSelected ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "bold" }}>{scopeReviewsSelected.label}</span>
            {scopeReviewsSelected.root_cause ? (
              <span
                style={{
                  fontSize: "12px",
                  color: theme === "dark" ? "#8c8c8c" : "#666",
                }}
              >
                {scopeReviewsSelected.root_cause}
              </span>
            ) : null}
          </div>
        ) : (
          "Reviews"
        )
      }
      onClose={() => {
        setScopeReviewsDrawerOpen(false);
        setScopeReviewsLoading(false);
        setScopeReviewsSelected(null);
        setScopeReviewsList([]);
      }}
      open={scopeReviewsDrawerOpen}
      width={welcomeReviewsDrawerWidth()}
    >
      <Spin spinning={scopeReviewsLoading}>
        <WelcomeDrawerReviewsList
          allReviews={scopeReviewsList}
          reviewsLoading={scopeReviewsLoading}
        />
      </Spin>
    </Drawer>
  );

  if (!viewportReached) {
    return (
      <div
        ref={lazyRootRef}
        className={styles.contentPreviewLoading}
        style={{ minHeight: 140 }}
        aria-busy="true"
      >
        <Spin size="small" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.contentPreviewLoading}>
        <Spin size="small" />
      </div>
    );
  }

  if (!widgetData) {
    if (
      isWelcomeGenAiContentMetricWidget(widget) &&
      welcomeWidgetJobIsProcessing(widget?.status)
    ) {
      return (
        <WelcomeWidgetGenaiPendingState
          t={t}
          widget={widget}
          className={`${styles.contentPreviewLoading} ${welcomeStyles.widgetGenaiPending}`}
          progressClassNames={welcomeWidgetJobProgressClassNames}
        />
      );
    }
    if (loadFailed) {
      return (
        <WelcomeWidgetDataStateMessage
          status={WELCOME_WIDGET_DATA_STATUS_FAILED}
          t={t}
          onRefresh={onRefresh}
          failedClassName={welcomeStyles.widgetDataLoadFailed}
        />
      );
    }
    return null;
  }

  const data = widgetData.data || {};
  const metricId = Number(widgetData.metric_id || widget.metric_id);
  const reprType = Number(widgetData.representation_type || widget.representation_type);
  const topicIds = Array.isArray(widgetData.topic_ids)
    ? widgetData.topic_ids.map((x) => Number(x)).filter((n) => n > 0)
    : [];
  const topicLabels = Array.isArray(widgetData.topic_labels)
    ? widgetData.topic_labels.filter((l) => l != null && String(l).trim() !== "")
    : [];
  const useRootCauseTopicTabs =
    reprType === 3 && metricId === 20 && topicLabels.length >= 1;

  const resolvedDataState = resolveWelcomeWidgetDataState(data, {
    queryFailed: loadFailed,
    metricId,
    widgetRow: widget,
  });
  const insufficientCounts = resolveWelcomeInsufficientReviewCounts(
    data,
    metricId,
    widget,
  );
  if (
    resolvedDataState === WELCOME_WIDGET_DATA_STATUS_FAILED &&
    isWelcomeGenAiContentMetricWidget(widget) &&
    welcomeWidgetJobIsProcessing(widget?.status)
  ) {
    return (
      <WelcomeWidgetGenaiPendingState
        t={t}
        widget={widget}
        className={`${styles.contentPreviewLoading} ${welcomeStyles.widgetGenaiPending}`}
        progressClassNames={welcomeWidgetJobProgressClassNames}
      />
    );
  }
  if (resolvedDataState === WELCOME_WIDGET_DATA_STATUS_FAILED) {
    return (
      <WelcomeWidgetDataStateMessage
        status={WELCOME_WIDGET_DATA_STATUS_FAILED}
        t={t}
        onRefresh={onRefresh}
        failedClassName={welcomeStyles.widgetDataLoadFailed}
      />
    );
  }
  if (resolvedDataState === WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT) {
    return (
      <WelcomeWidgetDataStateMessage
        status={WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT}
        t={t}
        insufficientCounts={insufficientCounts}
        emptyClassName={welcomeStyles.noDataMessage}
        insufficientClassName={styles.contentPreviewInsufficient}
      />
    );
  }
  if (
    resolvedDataState === WELCOME_WIDGET_DATA_STATUS_EMPTY &&
    !useRootCauseTopicTabs
  ) {
    return (
      <WelcomeWidgetInsufficientOrEmpty
        data={data}
        metricId={metricId}
        widgetRow={widget}
        t={t}
        className={welcomeStyles.noDataMessage}
        insufficientClassName={styles.contentPreviewInsufficient}
      />
    );
  }

  const apiError = data && typeof data.error === "string" ? data.error.trim() : "";
  if (apiError) {
    const errStatus = welcomeKpiErrorMeansNoData(apiError)
      ? WELCOME_WIDGET_DATA_STATUS_EMPTY
      : WELCOME_WIDGET_DATA_STATUS_FAILED;
    if (errStatus === WELCOME_WIDGET_DATA_STATUS_EMPTY) {
      return (
        <WelcomeWidgetInsufficientOrEmpty
          data={data}
          metricId={metricId}
          widgetRow={widget}
          t={t}
          className={welcomeStyles.noDataMessage}
          insufficientClassName={styles.contentPreviewInsufficient}
        />
      );
    }
    return (
      <WelcomeWidgetDataStateMessage
        status={errStatus}
        t={t}
        onRefresh={onRefresh}
        failedClassName={welcomeStyles.widgetDataLoadFailed}
      />
    );
  }

  // ── Narrative widgets (repr=4): General Summary, Key Drivers, Highlights ──
  if (reprType === 4) {
    if (welcomeWidgetShowsEmptyGenaiSignal(data, widget)) {
      return (
        <WelcomeWidgetInsufficientOrEmpty
          data={data}
          metricId={metricId}
          widgetRow={widget}
          t={t}
          className={welcomeStyles.noDataMessage}
          insufficientClassName={styles.contentPreviewInsufficient}
        />
      );
    }

    const narrative = data.narrative || {};
    let sumTabs = data.summary_tabs;

    // Metric 25: filter out global tab
    if (metricId === 25 && Array.isArray(sumTabs)) {
      sumTabs = sumTabs.filter(
        (tab) => tab && !["general", "global"].includes(String(tab.id || "").toLowerCase())
      );
    }

    const hasHierarchicalTabs =
      Array.isArray(sumTabs) &&
      WELCOME_HIERARCHICAL_SUMMARY_METRIC_IDS.has(metricId) &&
      (metricId === 25 ? sumTabs.length >= 1 : sumTabs.length > 1);

    if (hasHierarchicalTabs) {
      return (
        <>
          <WelcomeHierarchicalSummaryReadOnly
            tabs={sumTabs}
            metricId={metricId}
            widget={{
              ...widget,
              topic_ids:
                topicIds.length > 0
                  ? topicIds
                  : Array.isArray(widget?.industry_topic_ids)
                    ? widget.industry_topic_ids
                    : [],
              industry_topic_ids:
                Array.isArray(widget?.industry_topic_ids) &&
                widget.industry_topic_ids.length > 0
                  ? widget.industry_topic_ids
                  : topicIds,
              topic_labels: topicLabels,
            }}
            narrativeData={data}
            onTabChange={null}
            activeTabKey={undefined}
            onActiveTabKeyChange={null}
            parseTopicLibId={parseTopicLibIdFromWidgetTab}
            onTabReviewCountClick={
              metricId === WELCOME_GENERAL_SUMMARY_METRIC_ID
                ? ({ topicLibId, topicLabel, reviewCount }) => {
                    void fetchMetric21ScopeReviews({
                      topicLibId,
                      topicLabel,
                      reviewCount,
                    });
                  }
                : undefined
            }
          />
          {scopeReviewsDrawer}
        </>
      );
    }

    if (metricId === WELCOME_GENERAL_SUMMARY_METRIC_ID) {
      return (
        <WelcomeGeneralSummaryNarrative
          narrative={narrative}
          widgetName={widgetData.name || widget.name}
          topicScopeLabels={
            widgetData.topic_labels ??
            widget.topic_labels ??
            []
          }
          data={data}
          widgetRow={widget}
        />
      );
    }

    // Metric 25 (single/no tabs) or metric 23 fallback
    if (metricId === 25) {
      const combinedText = decodeBasicHtmlEntities(
        [narrative.summary, ...(Array.isArray(narrative.bullets) ? narrative.bullets : [])]
          .filter((x) => x != null && String(x).trim() !== "")
          .join("\n\n")
      );
      if (!combinedText.trim()) {
        return <div style={{ color: "#888", fontSize: 13 }}>{t("welcome.39")}</div>;
      }
      // Fall through to hierarchical with single tab
      return (
        <WelcomeHierarchicalSummaryReadOnly
          tabs={[{ id: "global", label: widgetData.name || widget.name, content: combinedText }]}
          metricId={metricId}
          widget={{ ...widget, topic_ids: topicIds, topic_labels: topicLabels }}
          narrativeData={data}
          onTabChange={null}
          activeTabKey={undefined}
          onActiveTabKeyChange={null}
        />
      );
    }

    // Metric 23: use narrative summary + bullets
    const summaryText = String(narrative.summary ?? "").trim();
    const bullets = Array.isArray(narrative.bullets)
      ? narrative.bullets.map((b) => String(b ?? "").trim()).filter(Boolean)
      : [];

    if (!summaryText && bullets.length === 0) {
      return <div style={{ color: "#888", fontSize: 13 }}>{t("welcome.39")}</div>;
    }
    return (
      <WelcomeGeneralSummaryNarrative
        narrative={narrative}
        widgetName={widgetData.name || widget.name}
        data={data}
        widgetRow={widget}
      />
    );
  }

  // ── Brand chart KPIs with topic tabs (intent, sentiment, participation, 10/29) ───
  if (
    Array.isArray(data.topic_tabs) &&
    data.topic_tabs.length > 0 &&
    (reprType === 1 || metricId === 10 || metricId === 29)
  ) {
    return <BrandKpiTopicTabsReadOnly data={data} theme={theme} metricId={metricId} widget={widget} reportId={reportId} />;
  }

  // ── Metric 15 (Topic Participation) reprType=1, chart_type="bar", no tabs ─
  if (metricId === 15 && data.chart_type === "bar") {
    return <WelcomeTopicParticipationBarChart payload={data} />;
  }

  // ── Single-chart brand KPIs (no topic_tabs): doughnut, dual rings, stacked bar ─
  if (reprType === 1 && data.chart_type === "topic_doughnut_grid") {
    const topics = data.topics || [];
    if (!topics.length) {
      return <div style={{ color: "#888", fontSize: 13 }}>{t("welcome.39")}</div>;
    }
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        {topics.map((topic, ti) => {
          const td = {
            labels: topic.labels,
            legend: topic.legend,
            datasets: topic.datasets,
          };
          const customLabels =
            topic.legend && Array.isArray(topic.legend)
              ? topic.legend.map((item) => item.count)
              : null;
          if (!td.datasets?.[0]?.data?.length) return null;
          return (
            <div
              key={`${topic.name || "t"}-${ti}`}
              style={{ textAlign: "center", maxWidth: 280 }}
            >
              <KPIDoughnutChart
                data={td}
                theme={theme}
                customLabels={customLabels}
                showLegend={metricId === 12}
              />
              <div style={{ marginTop: 8, fontWeight: 600 }}>{topic.name}</div>
            </div>
          );
        })}
      </div>
    );
  }
  if (reprType === 1 && data.chart_type === "doughnut") {
    const customLabels =
      data.legend && Array.isArray(data.legend)
        ? data.legend.map((item) => item.count)
        : null;
    if (!data.datasets?.[0]?.data?.length) {
      return <div style={{ color: "#888", fontSize: 13 }}>{t("welcome.39")}</div>;
    }
    return (
      <KPIDoughnutChart
        data={data}
        theme={theme}
        customLabels={customLabels}
        showLegend={metricId === 12}
      />
    );
  }
  if (reprType === 1 && data.chart_type === "dual_doughnut_intent") {
    return <WelcomeDualIntentDoughnutCharts data={data} theme={theme} />;
  }
  if (reprType === 1 && data.chart_type === "dual_doughnut_sentiment") {
    return <WelcomeDualSentimentDoughnutCharts data={data} theme={theme} />;
  }
  if (reprType === 1 && data.chart_type === "stacked_bar") {
    return metricId === 14 ? (
      <WelcomeTopicSentimentBarChart
        payload={data}
        widgetId={widget?.id}
        reportId={reportId}
      />
    ) : metricId === 12 ? (
      <WelcomeTopicIntentBarChart payload={data} />
    ) : (
      <KPIStackedBarChart data={data} theme={theme} />
    );
  }

  // Brand KPI hero (metrics 9, 10 & 29) — repr 1 bar payloads or legacy repr 3 list rows
  const brandKpiHeroHeader = resolveAiReportBrandKpiHeader(data, metricId);
  if (welcomeWidgetUsesBrandKpiHero({ metric_id: metricId }) && brandKpiHeroHeader) {
    return (
      <div
        className={`${welcomeStyles.chartContainer} ${welcomeStyles.brandKpiHeroContainer}`}
      >
        <WelcomeBrandKpiHeroCard
          metricId={metricId}
          header={brandKpiHeroHeader}
          widget={widget}
          since={widget.since}
          until={widget.until}
          t={t}
          i18n={i18n}
          isPageDataLoading={false}
        />
      </div>
    );
  }

  if (
    reprType === 1 &&
    (data.chart_type === "bar" || data.chart_type === "line")
  ) {
    if (
      !data.datasets?.[0]?.data?.length ||
      !data.labels?.length ||
      data.datasets.every((ds) => ds.data.every((val) => val === 0))
    ) {
      return (
        <div style={{ color: "#888", fontSize: 13 }}>{t("welcome.39")}</div>
      );
    }
    return <KPIBarChart data={data} theme={theme} />;
  }

  // ── Chart widgets: Decision Analysis, Hot Terms (check before reprType) ──
  if (data.chart_type === "decision_distribution") {
    return (
      <>
        <WelcomeDecisionDistribution
          data={data}
          theme={theme}
          widget={{ ...widget, metric_id: metricId }}
          onTopicTabLoad={null}
          activeTabKey={undefined}
          onActiveTabKeyChange={null}
          onDecisionSliceClick={
            metricId === 27
              ? (decisionKey, decisionTrue, topicLibId) => {
                  void fetchMetric27ReportReviews(
                    decisionKey,
                    decisionTrue,
                    topicLibId,
                  );
                }
              : undefined
          }
        />
        <Drawer
          {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
          title={
            decisionReviewsSelected ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontWeight: "bold" }}>{decisionReviewsSelected.label}</span>
                <span
                  style={{
                    fontSize: "12px",
                    color: theme === "dark" ? "#8c8c8c" : "#666",
                  }}
                >
                  {decisionReviewsSelected.root_cause}
                </span>
              </div>
            ) : (
              "Reviews"
            )
          }
          onClose={() => {
            setDecisionReviewsDrawerOpen(false);
            setDecisionReviewsLoading(false);
            setDecisionReviewsSelected(null);
            setDecisionReviewsList([]);
          }}
          open={decisionReviewsDrawerOpen}
          width={welcomeReviewsDrawerWidth()}
        >
          <Spin spinning={decisionReviewsLoading}>
            <WelcomeDrawerReviewsList
              allReviews={decisionReviewsList}
              reviewsLoading={decisionReviewsLoading}
            />
          </Spin>
        </Drawer>
      </>
    );
  }

  if (data.chart_type === "hotterms") {
    return (
      <WelcomeHotTermsWidget
        data={data}
        widget={{ ...widget, metric_id: metricId }}
        onTopicTabLoad={null}
        activeTabKey={undefined}
        onActiveTabKeyChange={null}
      />
    );
  }

  // ── Table widgets (repr=3): Root Cause ────────────────────────────────────
  if (reprType === 3) {
    const tableSchema = getWidgetTableSchema({ ...widget, metric_id: metricId }, t);
    if (tableSchema) {
      if (useRootCauseTopicTabs) {
        return (
          <>
            <WelcomeGenaiRootCauseTopicTabs
              topicIds={topicIds}
              topicLabels={topicLabels}
              topicReviewCounts={data?.topic_review_counts}
              sourceItems={Array.isArray(data.items) ? data.items : []}
              tableSchema={tableSchema}
              widget={{ ...widget, metric_id: metricId, topic_ids: topicIds, topic_labels: topicLabels }}
              groupId={null}
              setWidgets={null}
              setGroups={null}
              scopeData={data}
              onViewAssociatedReviews={fetchMetric20ReportReviews}
            />
            {rootCauseReviewsDrawer}
          </>
        );
      }
      if (!data.items || data.items.length === 0) {
        return (
          <div style={{ color: "#888", fontSize: 13 }}>
            {t("welcome.rootCauseNoMeaningfulFinding")}
          </div>
        );
      }
      const triadRows = normalizeRowsBySchema(data.items, tableSchema, {
        ...widget,
        metric_id: metricId,
      });
      return (
        <>
          <WelcomeRootCauseCardStack
            rows={triadRows}
            widgetId={widget.id}
            groupId={null}
            sourceItems={Array.isArray(data.items) ? data.items : []}
            setWidgets={null}
            setGroups={null}
            onViewAssociatedReviews={fetchMetric20ReportReviews}
          />
          {rootCauseReviewsDrawer}
        </>
      );
    }

    // Metric 15: topic participation {items:[{topic,count}]} — simple ranked list
    if (metricId === 15 && Array.isArray(data.items) && data.items.length > 0) {
      const maxCount = Math.max(...data.items.map((r) => r.count || 0)) || 1;
      return (
        <div style={{ padding: "4px 0" }}>
          {data.items.map((row, i) => (
            <div
              key={i}
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
            >
              <span style={{ flex: "0 0 180px", fontSize: 13, color: "#333", textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {decodeBasicHtmlEntities(row.topic || "")}
              </span>
              <div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 12, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.round((row.count / maxCount) * 100)}%`,
                    height: "100%",
                    background: "#6c47d5",
                    borderRadius: 4,
                  }}
                />
              </div>
              <span style={{ flex: "0 0 32px", fontSize: 12, color: "#888", textAlign: "right" }}>
                {row.count}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  }

  return null;
}

/** Metric 11 = sentiment distribution, 12 = intent — render side-by-side when both exist. */
const METRIC_SENTIMENT_DISTRIBUTION = 11;
const METRIC_INTENT_DISTRIBUTION = 12;

function buildWidgetLayoutPlan(widgets) {
  const list = Array.isArray(widgets) ? widgets : [];
  const idx11 = list.findIndex((w) => Number(w.metric_id) === METRIC_SENTIMENT_DISTRIBUTION);
  const idx12 = list.findIndex((w) => Number(w.metric_id) === METRIC_INTENT_DISTRIBUTION);
  if (idx11 < 0 || idx12 < 0) {
    return list.map((w) => ({ type: "single", widget: w }));
  }
  const w11 = list[idx11];
  const w12 = list[idx12];
  const pairOrder = [w11, w12].sort((a, b) => Number(a.metric_id) - Number(b.metric_id));
  const pairIds = new Set(pairOrder.map((w) => w.id));
  const insertAt = Math.min(idx11, idx12);
  const out = [];
  let pairEmitted = false;
  list.forEach((w, i) => {
    if (pairIds.has(w.id)) {
      if (!pairEmitted && i === insertAt) {
        out.push({ type: "pair", widgets: pairOrder });
        pairEmitted = true;
      }
      return;
    }
    out.push({ type: "single", widget: w });
  });
  return out;
}

// ─── Widget Card ─────────────────────────────────────────────────────────────
function WidgetCard({ widget, reportId, report, onEditSaved, highlighted = false }) {
  const { t } = useTranslation();
  const isEnterprisePowerUser = useEnterprisePowerUser();
  const userReducer = useSelector((state) => state.userReducer);
  const workspaceContentEditable = userReducer?.WorkspaceContentEditable === true;

  const [editOpen, setEditOpen] = useState(false);
  const [summaryTabsOpen, setSummaryTabsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [regenActive, setRegenActive] = useState(false);
  const [widgetKpiData, setWidgetKpiData] = useState(null);
  const regenIterRef = useRef(0);

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [widgetInfoPayload, setWidgetInfoPayload] = useState(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptEditorWidget, setPromptEditorWidget] = useState(null);

  const [regenerateQuotaModalOpen, setRegenerateQuotaModalOpen] = useState(false);
  const [regenerateQuotaWidget, setRegenerateQuotaWidget] = useState(null);
  const [regenerateQuotaResult, setRegenerateQuotaResult] = useState(null);
  const [regenerateQuotaLoading, setRegenerateQuotaLoading] = useState(false);
  const [regenConfirmLoading, setRegenConfirmLoading] = useState(false);

  const insufficientCounts = useMemo(
    () =>
      resolveWelcomeInsufficientReviewCounts(
        widgetKpiData?.data,
        widget.metric_id,
        widget,
      ),
    [widgetKpiData, widget],
  );
  const kpiDataLoaded = widgetKpiData != null;
  const reportScopeInsufficient = useMemo(() => {
    const reportRc = Number(report?.review_count);
    if (!Number.isFinite(reportRc)) return false;
    return reportRc < resolveWelcomeWidgetMinReviewCount(widget.metric_id);
  }, [report?.review_count, widget.metric_id]);
  const wStatus = statusLabel(widget.status);
  const jobBusy = wStatus === "pending" || wStatus === "in_progress";
  // While a job is re-running, ignore stale empty_genai / insufficient KPI so the old
  // terminal message does not sit under the loading panel.
  const emptyGenaiSignal =
    !jobBusy &&
    (welcomeWidgetShowsEmptyGenaiSignal(widgetKpiData?.data, widget) ||
      welcomeJobStatusDescriptionMeansEmptyGenaiOutput(
        widget.job_status_description,
      ));
  const failedJobShowsContent =
    !jobBusy && welcomeWidgetFailedJobShowsContentState(widget);
  const softTerminalState =
    !jobBusy &&
    (failedJobShowsContent ||
      emptyGenaiSignal ||
      (kpiDataLoaded &&
        welcomeWidgetShowsInsufficientState(
          widgetKpiData?.data,
          widget.metric_id,
          widget,
        )));
  const showInsufficientBadge =
    softTerminalState ||
    Boolean(insufficientCounts) ||
    reportScopeInsufficient;
  const isCompleted = wStatus === "completed";
  const showWidgetContent = (isCompleted || softTerminalState) && !jobBusy;
  const showFailedUi = wStatus === "failed" && !softTerminalState && !jobBusy;
  const stuckInProgress = isWelcomeWidgetJobStuckInProgress(widget);
  const showStuckRegenerate = canRegenerateStuckWidget(widget);
  const isHierarchical = hasHierarchicalSummaryTabs(widgetKpiData, widget.metric_id);
  const failedJobMessage = resolveWelcomeJobStatusDescription(
    widget.job_status_description,
    t,
  );

  const mergedMenuWidget = useMemo(() => {
    const repr = Number(
      widgetKpiData?.representation_type ?? widget.representation_type ?? 0,
    );
    return {
      ...widget,
      metricType: widgetKpiData?.metricType || "brand",
      representation_type: Number.isFinite(repr) ? repr : widget.representation_type,
      data: widgetKpiData?.data || {},
    };
  }, [widget, widgetKpiData]);

  const mergedForQuota = useMemo(
    () => ({
      ...widget,
      data: widgetKpiData?.data || {},
    }),
    [widget, widgetKpiData],
  );

  const widgetBaseName = useMemo(() => {
    return (
      welcomeMetricTitle(widget.metric_id, t) ||
      (widget.name && !/^Metric \d+$/.test(widget.name) ? widget.name : null) ||
      `Widget ${widget.id}`
    );
  }, [widget, t]);

  const widgetScoped = useMemo(
    () => ({
      ...widget,
      topic_labels: widgetKpiData?.topic_labels ?? widget.topic_labels,
      topic_scope_label:
        widgetKpiData?.topic_scope_label ?? widget.topic_scope_label,
    }),
    [widget, widgetKpiData],
  );

  /** Full line kept for menus / tags; header uses base + meta like My Workspace. */
  const widgetTitle = useMemo(
    () => buildWelcomeWidgetTitleLine(widgetScoped, widgetBaseName),
    [widgetScoped, widgetBaseName],
  );

  const widgetTitleMeta = useMemo(() => {
    if (!widgetTitle || widgetTitle === widgetBaseName) return "";
    const prefix = `${widgetBaseName} · `;
    if (widgetTitle.startsWith(prefix)) return widgetTitle.slice(prefix.length);
    return "";
  }, [widgetTitle, widgetBaseName]);

  /** Prefer last job completion; widget.created_at is first insert and stays stale after regen. */
  const widgetCreatedAtLabel = useMemo(() => {
    const ts = widget?.job_updated_at || widget?.created_at;
    if (!ts) return "";
    return (
      formatInUserTimezone(
        ts,
        report?.user_timezone_label || userReducer,
        "D MMMM YYYY, HH:mm",
      ) || ""
    );
  }, [
    widget?.job_updated_at,
    widget?.created_at,
    report?.user_timezone_label,
    userReducer,
  ]);

  const handleEditSaved = () => {
    setRefreshKey((k) => k + 1);
    onEditSaved();
  };

  useEffect(() => {
    if (!regenActive) return;
    regenIterRef.current = 0;
    const interval = setInterval(() => {
      regenIterRef.current += 1;
      setRefreshKey((k) => k + 1);
      if (regenIterRef.current >= 12) {
        clearInterval(interval);
        setRegenActive(false);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [regenActive]);

  const closeInfoModal = () => {
    setInfoModalOpen(false);
    setWidgetInfoPayload(null);
  };

  const handleShowInfo = async () => {
    try {
      const response = await apiRequest(
        "welcome/widget/info",
        "POST",
        { widget_id: widget.id },
        true,
      );
      if (response?.success) {
        setWidgetInfoPayload(
          mergeWidgetInfoPayload(response.success, widget, {
            widgetKpiData,
            report,
          }),
        );
        setInfoModalOpen(true);
      } else {
        notification.error({
          message: t("common.error"),
          description: response?.error || t("welcome.errorGettingInfo"),
        });
      }
    } catch {
      notification.error({
        message: t("common.error"),
        description: t("welcome.errorGettingInfo"),
      });
    }
  };

  const handleRenameOpen = () => {
    setRenameInput(widget.name || "");
    setRenameModalOpen(true);
  };

  const handleSaveRename = async () => {
    try {
      const response = await apiRequest(
        "welcome/widget/rename",
        "PUT",
        { widget_id: widget.id, name: renameInput },
        true,
      );
      if (response?.success) {
        notification.success({
          message: t("common.success"),
          description: t("welcome.widgetRenamedSuccess"),
        });
        setRenameModalOpen(false);
        onEditSaved();
      } else {
        notification.error({
          message: t("common.error"),
          description: response?.error || t("welcome.errorRenamingWidget"),
        });
      }
    } catch (e) {
      notification.error({
        message: t("common.error"),
        description: e?.message || t("welcome.errorRenamingWidget"),
      });
    }
  };

  const handleDeleteWidget = () => {
    Modal.confirm({
      title: t("welcome.widgetActionDelete"),
      content: t("welcome.deleteGroupWidgetsWarning"),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          const response = await apiRequest(
            "welcome/widget/delete",
            "DELETE",
            { widget_id: widget.id },
            true,
          );
          if (response?.success) {
            notification.success({
              message: t("common.success"),
              description: t("welcome.widgetDeletedSuccess"),
            });
            onEditSaved();
          } else {
            notification.error({
              message: t("common.error"),
              description: response?.error || t("welcome.widgetDeletedError"),
            });
          }
        } catch (e) {
          notification.error({
            message: t("common.error"),
            description: e?.message || t("welcome.errorDeletingWidget"),
          });
        }
      },
    });
  };

  const checkQuotaForRegenerateWidget = async (mergedW, { forceModal = false } = {}) => {
    const payload = buildAiReportRegenerateQuotaPayload(mergedW, report, widgetKpiData);
    if (!payload.dashboard_ids.length || !payload.start_date || !payload.end_date) {
      notification.error({
        message: t("common.error"),
        description: t("welcome.regenerateQuotaMissingScope"),
      });
      return;
    }
    if (!forceModal) {
      setRegenConfirmLoading(true);
    }
    setRegenerateQuotaLoading(true);
    if (forceModal) {
      setRegenerateQuotaWidget(mergedW);
      setRegenerateQuotaModalOpen(true);
      setRegenerateQuotaResult(null);
    }
    try {
      const response = await apiRequest(
        "subscriptions/check_quota_for_target_params",
        "POST",
        payload,
        true,
      );
      if (!forceModal && welcomeRegenerateCanProceedWithoutQuotaModal(response)) {
        await executeGenaiRequeueForWidget(mergedW);
        return;
      }
      setRegenerateQuotaWidget(mergedW);
      setRegenerateQuotaModalOpen(true);
      setRegenerateQuotaResult(response);
    } catch (err) {
      console.error(err);
      setRegenerateQuotaWidget(mergedW);
      setRegenerateQuotaModalOpen(true);
      setRegenerateQuotaResult({ error: true });
    } finally {
      setRegenerateQuotaLoading(false);
      if (!forceModal) {
        setRegenConfirmLoading(false);
      }
    }
  };

  const closeRegenerateQuotaModal = () => {
    setRegenerateQuotaModalOpen(false);
    setRegenerateQuotaWidget(null);
    setRegenerateQuotaResult(null);
    setRegenerateQuotaLoading(false);
  };

  const handleGenaiRequeue = () => {
    if (
      welcomeGenaiRegenerateSkipsCreditCharge(widget.status) ||
      welcomeRegenerateSkipsQuotaCheck(mergedForQuota)
    ) {
      void executeGenaiRequeueForWidget(mergedForQuota);
      return;
    }
    void checkQuotaForRegenerateWidget(mergedForQuota);
  };

  const executeGenaiRequeueForWidget = async (targetWidget) => {
    if (!targetWidget?.id) return;
    setRegenConfirmLoading(true);
    try {
      const response = await apiRequest(
        "welcome/widget/genai-requeue",
        "POST",
        { widget_id: targetWidget.id },
        true,
      );
      if (response?.success) {
        const isKpiOnlyRefresh =
          response.job_id == null || response.job_id === undefined;
        notification.success({
          message: isKpiOnlyRefresh
            ? t("welcome.regenerateSuccess")
            : t("welcome.genaiRequeueSuccess"),
        });
        closeRegenerateQuotaModal();
        if (!isKpiOnlyRefresh) {
          setRegenActive(true);
        }
        handleEditSaved();
        if (isKpiOnlyRefresh) {
          setRefreshKey((k) => k + 1);
        }
      } else {
        const errRaw = response && response.error;
        const isQuotaError =
          errRaw === "INSUFFICIENT_AI_CREDITS" ||
          (typeof errRaw === "string" && errRaw.toUpperCase().includes("CREDIT"));
        notification.error({
          message: t("common.error"),
          description: isQuotaError
            ? t("welcome.insufficientAiCredits")
            : typeof errRaw === "string"
              ? errRaw
              : t("welcome.genaiRequeueError"),
        });
      }
    } catch (error) {
      notification.error({
        message: t("common.error"),
        description: error.message || t("welcome.genaiRequeueError"),
      });
    } finally {
      setRegenConfirmLoading(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    if (!regenerateQuotaWidget?.id) return;
    await executeGenaiRequeueForWidget(regenerateQuotaWidget);
  };

  const openPromptEditor = () => {
    if (!widgetSupportsOrgLlmPromptAi(mergedMenuWidget)) return;
    setPromptEditorWidget({
      id: widget.id,
      name: widgetTitle,
      metric_id: widget.metric_id,
    });
    setPromptModalOpen(true);
  };

  const showEditInsight =
    isCompleted &&
    canEditInsightAiReport(mergedMenuWidget, isHierarchical) &&
    workspaceContentEditable;
  const showEditSummaryTabs = isCompleted && isHierarchical;

  const dropdownItems = [
    {
      key: "info",
      label: t("welcome.widgetActionInfo"),
      onClick: () => {
        void handleShowInfo();
      },
      icon: <InfoCircleOutlined />,
    },
    {
      key: "rename",
      label: t("welcome.widgetActionRename"),
      onClick: handleRenameOpen,
      icon: <EditOutlined />,
    },
    ...(showEditInsight
      ? [
          {
            key: "edit-content",
            label: t("welcome.infoEditContent"),
            icon: <EditOutlined />,
            onClick: () => setEditOpen(true),
          },
        ]
      : []),
    ...(showEditSummaryTabs
      ? [
          {
            key: "edit-summary-tabs",
            label: t("welcome.editSummaryTabs"),
            icon: <FileTextOutlined />,
            onClick: () => setSummaryTabsOpen(true),
          },
        ]
      : []),
    ...(widgetSupportsOrgLlmPromptAi(mergedMenuWidget) && isEnterprisePowerUser
      ? [
          {
            key: "editPrompt",
            label: t("welcome.editPrompt"),
            onClick: openPromptEditor,
            icon: <EditOutlined />,
          },
        ]
      : []),
    {
      key: "refresh-widget-data",
      label: t("welcome.refreshWidgetData"),
      icon: <SyncOutlined />,
      title: t("welcome.refreshWidgetDataHint"),
      disabled: jobBusy || regenActive,
      onClick: () => {
        setRefreshKey((k) => k + 1);
        notification.success({
          message: t("common.success"),
          description: t("welcome.widgetRefreshed"),
        });
      },
    },
    ...(isAiReportRegenerableWidget(widget) &&
    (isCompleted || showStuckRegenerate)
      ? [
          {
            key: "regenerate",
            label: t("welcome.regenerate"),
            icon: <ReloadOutlined />,
            disabled: (jobBusy && !showStuckRegenerate) || regenActive,
            onClick: handleGenaiRequeue,
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "delete",
      label: t("welcome.widgetActionDelete"),
      danger: true,
      onClick: handleDeleteWidget,
      icon: <CloseOutlined />,
    },
  ];

  return (
    <div
      className={[
        styles.widgetCard,
        highlighted && styles.widgetCardHighlighted,
        welcomeWidgetUsesDoubleGridColumn(widget) && welcomeStyles.widgetCard,
        welcomeWidgetUsesDoubleGridColumn(widget) &&
          welcomeStyles.welcomeDoubleGridColumn,
      ]
        .filter(Boolean)
        .join(" ")}
      data-engagement-widget={widget.id}
    >
      <div className={welcomeStyles.widgetHeader}>
        <div className={welcomeStyles.widgetTitleSection}>
          <div className={welcomeStyles.widgetTitleRow}>
            <InfoCircleOutlined
              onClick={() => {
                void handleShowInfo();
              }}
              className={welcomeStyles.infoIcon}
            />
            <div className={welcomeStyles.widgetTitleText}>
              <h4 className={welcomeStyles.widgetTitlePrimary}>
                <span className={welcomeStyles.widgetTitleName}>
                  {widgetBaseName}
                </span>
                {widgetCreatedAtLabel ? (
                  <span className={welcomeStyles.widgetTitleDateRange}>
                    {t("ai_services.created_at_label")}: {widgetCreatedAtLabel}
                  </span>
                ) : null}
              </h4>
              {widgetTitleMeta ? (
                <p className={welcomeStyles.widgetTitleMeta}>{widgetTitleMeta}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className={welcomeStyles.widgetHeaderActions}>
          {jobBusy && (
            <div
              className={`${welcomeStyles.statusBadge} ${welcomeStyles.statusLoading}`}
            >
              <Spin size="small" style={{ marginRight: "6px" }} />
              {t("welcome.widgetBadgeLoading")}
            </div>
          )}
          {showFailedUi && (
            <div
              className={`${welcomeStyles.statusBadge} ${welcomeStyles.statusError}`}
            >
              ⚠ {t("welcome.widgetBadgeFailed")}
            </div>
          )}
          {showInsufficientBadge && !jobBusy ? (
            <div
              className={`${welcomeStyles.statusBadge} ${welcomeStyles.statusInsufficient}`}
            >
              {t("welcome.widgetBadgeInsufficient")}
            </div>
          ) : null}
          {showStuckRegenerate && jobBusy && (
            <Tooltip title={t("ai_services.stuck_widget_regen_tooltip")}>
              <Button
                size="small"
                type="default"
                icon={<ReloadOutlined />}
                loading={regenConfirmLoading}
                disabled={regenActive}
                onClick={handleGenaiRequeue}
                className={styles.stuckRegenBtn}
              >
                {t("welcome.regenerate")}
              </Button>
            </Tooltip>
          )}
          {isCompleted && kpiDataLoaded && !showInsufficientBadge ? (
            <div
              className={`${welcomeStyles.statusBadge} ${welcomeStyles.statusReady}`}
            >
              ✓ {t("welcome.widgetBadgeReady")}
            </div>
          ) : null}
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="widget-actions-dropdown"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              className={welcomeStyles.moreButton}
            />
          </Dropdown>
        </div>
      </div>

      <div className={styles.widgetMeta}>
        <Tag className={styles.metricTag}>{widgetTitle}</Tag>
        {wStatus && !isCompleted && !jobBusy && !softTerminalState && (
          <Tag
            color={wStatus === "failed" ? "error" : "processing"}
            className={styles.statusTag}
          >
            {wStatus}
          </Tag>
        )}
      </div>

      {jobBusy && (
        <div className={styles.contentPreview}>
          {isWelcomeGenAiContentMetricWidget(widget) ? (
            <WelcomeWidgetGenaiPendingState
              t={t}
              widget={widget}
              className={`${styles.contentPreviewLoading} ${welcomeStyles.widgetGenaiPending}`}
              progressClassNames={welcomeWidgetJobProgressClassNames}
            />
          ) : (
            <div className={styles.contentPreviewLoading}>
              <Spin size="small" />
            </div>
          )}
          {stuckInProgress && (
            <>
              <p className={styles.stuckRegenHint}>{t("ai_services.stuck_widget_hint")}</p>
              {showStuckRegenerate ? (
                <p className={styles.stuckRegenHint}>
                  {t("welcome.regenerateRetryNoCredit")}
                </p>
              ) : null}
            </>
          )}
        </div>
      )}

      {showFailedUi && !isCompleted && (
        <div className={styles.stuckRegenPanel}>
          {failedJobMessage ? (
            <p className={styles.stuckRegenError}>{failedJobMessage}</p>
          ) : null}
          {showStuckRegenerate ? (
            <>
              <p className={styles.stuckRegenHint}>
                {t("welcome.regenerateRetryNoCredit")}
              </p>
              <Button
                icon={<ReloadOutlined />}
                loading={regenConfirmLoading}
                disabled={regenActive}
                onClick={handleGenaiRequeue}
              >
                {t("welcome.regenerate")}
              </Button>
            </>
          ) : null}
        </div>
      )}

      {showWidgetContent && (
        <div className={styles.contentPreview}>
          <WelcomeWidgetBody
            key={widget.id}
            reportId={reportId}
            widget={widget}
            refreshKey={refreshKey}
            onDataLoaded={setWidgetKpiData}
            onRefresh={() => setRefreshKey((k) => k + 1)}
          />
          {welcomeWidgetSupportsGenaiFeedback(mergedMenuWidget) ? (
            <WelcomeGenaiFeedback
              widget={mergedMenuWidget}
              extraWidgetContext={{
                source: "ai_report",
                ai_report_id: reportId,
              }}
              disabled={jobBusy}
              variant="footer"
            />
          ) : null}
        </div>
      )}

      <WelcomeWidgetInfoModal
        open={infoModalOpen && !!widgetInfoPayload}
        widgetInfo={widgetInfoPayload}
        widgetRow={widget}
        onClose={closeInfoModal}
      />

      <Modal
        title={t("welcome.editWidgetName")}
        open={renameModalOpen}
        onOk={handleSaveRename}
        onCancel={() => setRenameModalOpen(false)}
      >
        <Input
          value={renameInput}
          onChange={(e) => setRenameInput(e.target.value)}
          placeholder={t("welcome.enterWidgetName")}
        />
      </Modal>

      <WelcomeOrgPromptEditorModal
        open={promptModalOpen}
        onClose={() => {
          setPromptModalOpen(false);
          setPromptEditorWidget(null);
        }}
        widget={promptEditorWidget}
      />

      <WelcomeWidgetRegenerateQuotaModal
        open={regenerateQuotaModalOpen}
        onCancel={closeRegenerateQuotaModal}
        onConfirm={handleConfirmRegenerate}
        confirmLoading={regenConfirmLoading}
        regenerateQuotaLoading={regenerateQuotaLoading}
        regenerateQuotaResult={regenerateQuotaResult}
        onRecheck={(w) => {
          void checkQuotaForRegenerateWidget(w, { forceModal: true });
        }}
        widgetDisplayName={widgetTitle}
        quotaWidget={regenerateQuotaWidget}
        buildPayload={(w) => buildAiReportRegenerateQuotaPayload(w, report, widgetKpiData)}
      />

      {editOpen && (
        <EditInsightModal
          reportId={reportId}
          widget={widget}
          onClose={() => setEditOpen(false)}
          onSaved={handleEditSaved}
          contentEditable={workspaceContentEditable}
        />
      )}
      {summaryTabsOpen && (
        <EditSummaryTabsModal
          reportId={reportId}
          widget={widget}
          onClose={() => setSummaryTabsOpen(false)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
const AiReportDetail = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { reportId } = useParams();
  const userReducer = useSelector((state) => state.userReducer);
  const sessionDashboardName = String(
    useSelector((state) => state.DashboardReducer?.dashboard?.Name) ?? "",
  ).trim();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenWidgetsLoading, setRegenWidgetsLoading] = useState(false);
  const pollRef = useRef(null);
  const [pdfProgressAnim, setPdfProgressAnim] = useState(0);
  const pdfProgressRef = useRef(null);
  const [reportInfoOpen, setReportInfoOpen] = useState(false);
  const reportDeepLink = useMemo(
    () => readWorkspaceDeepLinkFromSearch(location.search),
    [location.search],
  );
  const reportDeepLinkAppliedRef = useRef(false);
  const highlightTimerRef = useRef(null);
  const [highlightWidgetId, setHighlightWidgetId] = useState(null);

  const scrollToReportWidget = useCallback((widgetId) => {
    const wid = Number(widgetId);
    if (!Number.isFinite(wid) || wid <= 0) return;
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-engagement-widget="${wid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    setHighlightWidgetId(wid);
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = setTimeout(() => {
      setHighlightWidgetId(null);
      highlightTimerRef.current = null;
    }, 5000);
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      const res = await apiRequest(
        buildAiReportApiRequestPath(
          reportId,
          "",
          reportDeepLink.workspaceUserId,
        ),
        "GET",
      );
      if (res?.success) {
        setReport(res.success);
      }
    } catch {
      notification.error({ message: t("ai_services.load_error") });
    } finally {
      setLoading(false);
    }
  }, [reportId, reportDeepLink.workspaceUserId, t]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchReport();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchReport]);

  useEffect(() => {
    if (!report) return;
    const id = report.id ?? reportId;
    if (!id) return;
    trackAiReportOpenedOnce(id, {
      brand: report.brand_name || report.brand,
      status: report.status,
    });
  }, [report, reportId]);

  const reportPollKey = useMemo(() => reportPollActivityKey(report), [report]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!reportPollKey) return undefined;
    if (reportPollNeedsPolling(reportPollKey)) {
      pollRef.current = setInterval(fetchReport, POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [reportPollKey, fetchReport]);

  const widgetLayoutPlan = useMemo(
    () => buildWidgetLayoutPlan(report?.widgets),
    [report?.widgets]
  );

  useEffect(() => {
    reportDeepLinkAppliedRef.current = false;
  }, [location.search, reportId]);

  useEffect(() => {
    const widgetId = reportDeepLink.widgetId;
    if (widgetId == null || !report?.widgets?.length) return;
    if (reportDeepLinkAppliedRef.current) return;
    if (!report.widgets.some((widget) => Number(widget.id) === Number(widgetId))) {
      return;
    }
    reportDeepLinkAppliedRef.current = true;
    scrollToReportWidget(widgetId);
  }, [report?.widgets, reportDeepLink.widgetId, scrollToReportWidget]);

  const handleRegenerate = async () => {
    setRegenLoading(true);
    try {
      const res = await apiRequest(
        aiReportApiUrl(reportId, "/regenerate-pdf"),
        "POST",
        {}
      );
      if (res?.success) {
        message.success(t("ai_services.regen_started"));
        fetchReport();
      } else {
        message.error(res?.error || t("ai_services.save_error"));
      }
    } catch {
      message.error(t("ai_services.save_error"));
    } finally {
      setRegenLoading(false);
    }
  };

  const handleRegenerateWidgets = async () => {
    setRegenWidgetsLoading(true);
    try {
      const res = await apiRequest(
        aiReportApiUrl(reportId, "/regenerate"),
        "POST",
        {}
      );
      if (res?.success) {
        message.success(t("ai_services.regen_started"));
        fetchReport();
      } else {
        message.error(
          typeof res?.error === "string" ? res.error : t("ai_services.save_error")
        );
      }
    } catch {
      message.error(t("ai_services.save_error"));
    } finally {
      setRegenWidgetsLoading(false);
    }
  };

  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await apiRequest(
        aiReportApiUrl(reportId),
        "DELETE"
      );
      if (res?.success) {
        message.success(t("ai_services.report_deleted"));
        history.push("/console/ai-services/auto-reports");
      } else {
        message.error(t("ai_services.delete_failed"));
      }
    } catch {
      message.error(t("ai_services.delete_failed"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const isRegenerating =
    regenLoading ||
    report?.status === "finalizing" ||
    report?.pdf_status === "Pending" ||
    report?.pdf_status === "Processing";


  // Smoothly animate displayed progress toward the real value from DB
  const pdfCompleted =
    report?.pdf_completed_widgets ?? report?.completed_widgets ?? 0;
  const pdfTotal = report?.pdf_total_widgets ?? report?.total_widgets ?? 0;
  const widgetsExportDone = pdfTotal > 0 && pdfCompleted >= pdfTotal;
  const realProgress = Math.min(
    100,
    report?.pdf_progress ||
      (pdfTotal > 0 ? Math.round((pdfCompleted * 100) / pdfTotal) : 0),
  );
  useEffect(() => {
    if (pdfProgressRef.current) clearInterval(pdfProgressRef.current);
    if (realProgress <= 0) {
      setPdfProgressAnim(0);
      return;
    }
    if (realProgress >= 100) {
      setPdfProgressAnim(100);
      return;
    }
    pdfProgressRef.current = setInterval(() => {
      setPdfProgressAnim((prev) => {
        const diff = realProgress - prev;
        if (Math.abs(diff) <= 1) {
          clearInterval(pdfProgressRef.current);
          return realProgress;
        }
        const next = prev + diff * 0.3;
        if (Math.round(next) === prev) {
          clearInterval(pdfProgressRef.current);
          return realProgress;
        }
        return Math.round(next);
      });
    }, 80);
    return () => clearInterval(pdfProgressRef.current);
  }, [realProgress]);

  const pdfProgressLabel = useMemo(() => {
    if (report?.pdf_status === "Ready" && report?.status === "finalizing") {
      return t("ai_services.pdf_finalizing_sync");
    }
    if (widgetsExportDone && report?.pdf_status === "Processing") {
      return t("ai_services.pdf_assembly_phase");
    }
    return report?.pdf_step || t("ai_services.regen_in_progress");
  }, [report?.pdf_status, report?.status, report?.pdf_step, widgetsExportDone, t]);

  const reportInfoCoverage = useMemo(
    () => formatAiReportCoverageLabel(report),
    [report],
  );

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className={styles.errorPage}>
        <WarningOutlined />
        <p>{t("ai_services.report_not_found")}</p>
        <Button onClick={() => history.push("/console/ai-services/auto-reports")}>
          {t("back")}
        </Button>
      </div>
    );
  }

  const isReady = reportDisplayStatus(report) === "ready";
  const hasPdf = !!report.pdf_url;
  const pdfStale = !!report.pdf_stale;
  const widgetsBusy = Number(report.widgets_in_progress || 0) > 0;

  return (
    <div className={styles.page}>
      {pdfStale && !isRegenerating ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            widgetsBusy
              ? t("ai_services.pdf_stale_banner_busy")
              : t("ai_services.pdf_stale_banner")
          }
        />
      ) : null}
      {/* Top bar */}
      <div className={styles.topBar}>
        <Button
          icon={<ArrowLeftOutlined />}
          type="text"
          onClick={() => history.push("/console/ai-services/auto-reports")}
          className={styles.backBtn}
        >
          {t("ai_services.reports_title")}
        </Button>

        <div className={styles.topBarActions}>
          <Popconfirm
            title={t("ai_services.delete_confirm_title")}
            description={t("ai_services.delete_confirm_desc")}
            onConfirm={handleDelete}
            okText={t("yes")}
            cancelText={t("no")}
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading}
              className={styles.deleteBtn}
            >
              {t("ai_services.delete_report_btn")}
            </Button>
          </Popconfirm>
          {hasPdf && (
            <Tooltip
              title={
                isRegenerating
                  ? t("ai_services.regen_in_progress")
                  : pdfStale
                    ? t("ai_services.pdf_stale_banner")
                    : undefined
              }
            >
              <Button
                icon={<DownloadOutlined />}
                disabled={isRegenerating}
                onClick={() => window.open(report.pdf_url, "_blank")}
              >
                {pdfStale && !isRegenerating
                  ? t("ai_services.download_stale_pdf")
                  : t("ai_services.download_pdf")}
              </Button>
            </Tooltip>
          )}
          {(isReady || reportDisplayStatus(report) === "finalizing") && (
            <div className={styles.regenBtnGroup}>
              <Tooltip
                title={
                  isRegenerating
                    ? t("ai_services.regen_in_progress")
                    : t("ai_services.regen_desc")
                }
              >
                <Button
                  type="primary"
                  icon={isRegenerating ? <SyncOutlined spin /> : <FilePdfOutlined />}
                  loading={regenLoading}
                  disabled={isRegenerating}
                  onClick={handleRegenerate}
                  className={styles.regenBtn}
                >
                  {isRegenerating ? t("ai_services.generating") : t("ai_services.regenerate_pdf")}
                </Button>
              </Tooltip>
              {!isRegenerating && report.pdf_generated_at && (
                <span className={styles.pdfGeneratedAt}>
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} />
                  {formatInUserTimezone(
                    report.pdf_generated_at,
                    report.user_timezone_label || userReducer,
                    "D MMM YYYY, HH:mm",
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Report header */}
      <div className={styles.reportHeader}>
        <div>
          <div className={styles.reportTitleRow}>
            <h2 className={styles.reportTitle}>{report.brand_name}</h2>
            <Tooltip title={t("ai_services.report_info_tooltip")}>
              <button
                type="button"
                className={styles.reportInfoBtn}
                aria-label={t("ai_services.report_info_tooltip")}
                onClick={() => setReportInfoOpen(true)}
              >
                <InfoCircleOutlined />
              </button>
            </Tooltip>
          </div>
          <p className={styles.reportSubtitle}>
            {report.report_month ? dayjs(report.report_month).format("MMMM YYYY") : "—"}
            {report.pivot_key && (
              <span className={styles.pivotBadge}>{report.pivot_key}</span>
            )}
          </p>
          <div className={styles.reportMeta}>
            {(report.data_since ||
              report.data_until ||
              report.coverage_since ||
              report.coverage_until ||
              report.report_month) && (
              <span className={styles.reportMetaItem}>
                <span className={styles.reportMetaLabel}>
                  {t("ai_services.coverage_period")}:
                </span>
                {formatAiReportCoverageLabel(report)}
              </span>
            )}
            {report.created_at && (
              <span className={styles.reportMetaItem}>
                <span className={styles.reportMetaLabel}>
                  {t("ai_services.created_at_label")}:
                </span>
                {formatInUserTimezone(
                  report.created_at,
                  report.user_timezone_label || userReducer,
                  "D MMMM YYYY, HH:mm",
                )}
              </span>
            )}
            {report.review_count != null && (
              <span className={styles.reportMetaReview}>
                <MessageOutlined aria-hidden />
                {t("ai_services.review_count_meta", {
                  count: formatAppNumber(report.review_count, i18n.language),
                })}
              </span>
            )}
          </div>
          <AiReportWidgetBreakdownChips
            report={report}
            widgets={report.widgets}
            className={styles.widgetBreakdownRow}
            onChipNavigate={(_category, widget) => scrollToReportWidget(widget?.id)}
          />
        </div>
        <div className={styles.statusArea}>
          {isRegenerating && (
            <div className={styles.pdfProgressWrap}>
              <div className={styles.pdfProgressHeader}>
                <SyncOutlined spin style={{ marginRight: 6 }} />
                <span>{pdfProgressLabel}</span>
                {pdfTotal > 0 ? (
                  <span className={styles.pdfProgressWidgets}>
                    {t("ai_services.pdf_widgets_progress", {
                      completed: pdfCompleted,
                      total: pdfTotal,
                      remaining: Math.max(0, pdfTotal - pdfCompleted),
                    })}
                  </span>
                ) : null}
                <span className={styles.pdfProgressPct}>
                  {pdfProgressAnim > 0 ? `${pdfProgressAnim}%` : ""}
                </span>
              </div>
              <div className={styles.pdfProgressTrack}>
                <div
                  className={styles.pdfProgressBar}
                  style={{ width: `${pdfProgressAnim}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Widgets */}
      {reportHasActiveWidgetJobs(report) && report.widgets?.length > 0 && (
        <div className={styles.reportStatusNotice}>
          <SyncOutlined spin className={styles.reportStatusNoticeIcon} />
          <div className={styles.reportStatusNoticeContent}>
            <p className={styles.reportStatusNoticeText}>
              {t("ai_services.stuck_widgets_hint")}
            </p>
          </div>
        </div>
      )}

      {reportDisplayStatus(report) === "insufficient_data" &&
        !reportHasActiveWidgetJobs(report) && (
        <div className={styles.reportStatusNotice}>
          <InfoCircleOutlined className={styles.reportStatusNoticeIcon} />
          <div className={styles.reportStatusNoticeContent}>
            <p className={styles.reportStatusNoticeText}>
              {t("ai_services.insufficient_data_report_hint")}
            </p>
          </div>
        </div>
      )}

      {reportDisplayStatus(report) === "partial" && !reportHasActiveWidgetJobs(report) && (
        <div className={styles.reportStatusNotice}>
          <InfoCircleOutlined className={styles.reportStatusNoticeIcon} />
          <div className={styles.reportStatusNoticeContent}>
            <p className={styles.reportStatusNoticeText}>
              {t("ai_services.partial_report_hint")}
            </p>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={regenWidgetsLoading}
              onClick={handleRegenerateWidgets}
              className={styles.openBtn}
            >
              {t("ai_services.regenerate_widgets")}
            </Button>
          </div>
        </div>
      )}

      {!report.widgets?.length ? (
        <div className={styles.emptyWidgets}>
          <ClockCircleOutlined style={{ fontSize: 32, color: "#888" }} />
          <p>{t("ai_services.no_widgets_yet")}</p>
        </div>
      ) : (
        <div className={styles.widgetsGrid}>
          {widgetLayoutPlan.map((item) =>
            item.type === "pair" ? (
              <div
                key={`pair-sentiment-intent-${item.widgets.map((w) => w.id).join("-")}`}
                className={styles.widgetsPairRow}
              >
                {item.widgets.map((w) => (
                  <WidgetCard
                    key={w.id}
                    widget={w}
                    reportId={reportId}
                    report={report}
                    onEditSaved={fetchReport}
                    highlighted={Number(highlightWidgetId) === Number(w.id)}
                  />
                ))}
              </div>
            ) : (
              <WidgetCard
                key={item.widget.id}
                widget={item.widget}
                reportId={reportId}
                report={report}
                onEditSaved={fetchReport}
                highlighted={
                  Number(highlightWidgetId) === Number(item.widget.id)
                }
              />
            ),
          )}
        </div>
      )}

      <Modal
        title={t("ai_services.report_info_modal_title")}
        open={reportInfoOpen}
        onCancel={() => setReportInfoOpen(false)}
        footer={
          <Button type="primary" onClick={() => setReportInfoOpen(false)}>
            {t("welcome.infoClose")}
          </Button>
        }
        width={520}
        destroyOnClose
      >
        <div className={styles.reportInfoModalGrid}>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.report_brand_label")}
            </span>
            <span className={styles.reportInfoModalValue}>
              {report.brand_name ?? "—"}
            </span>
          </div>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.report_id_label")}
            </span>
            <span className={styles.reportInfoModalValue}>
              {report.id != null ? String(report.id) : "—"}
            </span>
          </div>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.report_status_label")}
            </span>
            <span className={styles.reportInfoModalValue}>
              {reportDisplayStatus(report)
                ? t(`ai_services.status_${reportDisplayStatus(report)}`, {
                    defaultValue: reportDisplayStatus(report),
                  })
                : "—"}
            </span>
          </div>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.report_month")}
            </span>
            <span className={styles.reportInfoModalValue}>
              {report.report_month
                ? dayjs(report.report_month).format("MMMM YYYY")
                : "—"}
            </span>
          </div>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.coverage_period")}
            </span>
            <span className={styles.reportInfoModalValue}>{reportInfoCoverage}</span>
          </div>
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.created_at_label")}
            </span>
            <span className={styles.reportInfoModalValue}>
              {report.created_at
                ? formatInUserTimezone(
                    report.created_at,
                    report.user_timezone_label || userReducer,
                    "D MMMM YYYY HH:mm",
                  )
                : "—"}
            </span>
          </div>
          {(report.metric_ids || []).length > 0 ? (
            <div className={styles.reportInfoModalRow}>
              <span className={styles.reportInfoModalLabel}>
                {t("ai_services.metrics")}
              </span>
              <div className={styles.reportInfoMetricChips}>
                {(report.metric_ids || []).map((mid) =>
                  renderReportInfoMetricChip(report, mid, t),
                )}
              </div>
            </div>
          ) : null}
          {report.widgets_total != null && report.widgets_total > 0 ? (
            <div className={styles.reportInfoModalRow}>
              <span className={styles.reportInfoModalLabel}>
                {t("ai_services.widgets_progress_label")}
              </span>
              <span className={styles.reportInfoModalValue}>
                {formatWidgetBreakdownLabel(report, t)}
              </span>
            </div>
          ) : null}
          {report.pivot_key ? (
            <div className={styles.reportInfoModalRow}>
              <span className={styles.reportInfoModalLabel}>
                {t("ai_services.pivot_key_title")}
              </span>
              <span className={styles.reportInfoModalValue}>
                {String(report.pivot_key)}
              </span>
            </div>
          ) : null}
          {report.pivot_value ? (
            <div className={styles.reportInfoModalRow}>
              <span className={styles.reportInfoModalLabel}>
                {t("ai_services.report_pivot_value_label")}
              </span>
              <span className={styles.reportInfoModalValue}>
                {String(report.pivot_value)}
              </span>
            </div>
          ) : null}
          {report.review_count != null ? (
            <div className={styles.reportInfoModalRow}>
              <span className={styles.reportInfoModalLabel}>
                {t("ai_services.review_count_label_short")}
              </span>
              <span className={styles.reportInfoModalValue}>
                {formatAppNumber(report.review_count, i18n.language)}
              </span>
            </div>
          ) : null}
          <div className={styles.reportInfoModalRow}>
            <span className={styles.reportInfoModalLabel}>
              {t("ai_services.reports_detail_dashboard_label")}
            </span>
            <span
              className={styles.reportInfoModalValue}
              title={reportDashboardContextTitle(report, sessionDashboardName, t)}
            >
              {reportDashboardContextPrimary(report, sessionDashboardName) || "—"}
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AiReportDetail;
